import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { ChevronLeft, ChevronRight, Rocket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface HeadlineABWizardFooterProps {
  currentStep: number;
  totalSteps: number;
  canGoNext: boolean;
  onBack: () => void;
  onNext: () => void;
  onFinish?: () => void;
  isFinishing?: boolean;
  disabledReason?: string;
}

const getNextButtonText = (currentStep: number, isFinalStep: boolean) => {
  if (isFinalStep) return translateInterfaceText("Iniciar Teste");
  switch (currentStep) {
    case 1:
      return translateInterfaceText("Próximo: Ordem");
    case 2:
      return translateInterfaceText("Próximo: Tráfego");
    default:
      return translateInterfaceText("Continuar");
  }
};

const getTooltipText = (currentStep: number, customReason?: string) => {
  // Se houver razão customizada (ex: distribuição ≠ 100%), usar ela
  if (customReason) return customReason;
  switch (currentStep) {
    case 1:
      return translateInterfaceText("Crie pelo menos 2 variações para continuar");
    case 2:
      return translateInterfaceText("Defina a ordem das variações");
    case 3:
      return translateInterfaceText("A distribuição precisa totalizar 100%");
    default:
      return translateInterfaceText("Complete esta etapa para continuar");
  }
};

export function HeadlineABWizardFooter({
  currentStep,
  totalSteps,
  canGoNext,
  onBack,
  onNext,
  onFinish,
  isFinishing,
  disabledReason
}: HeadlineABWizardFooterProps) {
  useLanguage();
  const isFinalStep = currentStep === totalSteps;
  const canGoBack = currentStep > 1;
  const nextButtonText = getNextButtonText(currentStep, isFinalStep);
  const tooltipText = getTooltipText(currentStep, disabledReason);

  const NextButton = ({ className, fullWidth = false }: { className?: string; fullWidth?: boolean }) => { useLanguage(); return ((
    <Button
      onClick={isFinalStep ? onFinish : onNext}
      disabled={!canGoNext || isFinishing}
      className={cn(
        fullWidth ? (canGoBack ? "w-1/2" : "w-full") : "min-w-[140px]",
        "h-[40px] px-4 bg-[#1D4FFF] hover:bg-[#1A47E5] active:bg-[#1640D0] text-white font-medium rounded-[8px] gap-2 text-[13px] transition-all duration-200",
        className
      )}
    >
      {isFinishing ? (
        translateInterfaceText("Iniciando...")
      ) : (
        <>
          {isFinalStep && <Rocket className="h-4 w-4" />}
          <span>{nextButtonText}</span>
          {!isFinalStep && <ChevronRight className="h-4 w-4" />}
        </>
      )}
    </Button>
  )); };

  return (
    <div className="sticky bottom-0 z-10 bg-white dark:bg-background border-t border-[#E5E7EB] dark:border-border shadow-[0_-2px_8px_rgba(0,0,0,0.04)]">
      <div className="px-6 py-4 min-h-[72px] flex flex-col justify-center">
        {/* Mobile */}
        <div className="flex flex-col gap-2 lg:hidden">
          {/* Step indicator - mobile */}
          <div className="flex justify-center">
            <span className="text-[11px] text-[#9CA3AF] font-medium">
              {translateInterfaceText("Etapa")}{" "}{currentStep} {" "}{translateInterfaceText("de")}{" "}{totalSteps}
            </span>
          </div>

          {/* Buttons row */}
          <div className="flex flex-row gap-3">
            {canGoBack && (
              <Button
                variant="outline"
                onClick={onBack}
                className="h-[40px] px-4 bg-white border border-[#E8E8E8] text-[#6B7280] font-medium rounded-[8px] text-[13px]"
              >
                <ChevronLeft className="mr-1.5 h-4 w-4" />
                {translateInterfaceText("Voltar")}</Button>
            )}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className={cn(canGoBack ? "w-full" : "w-full")}>
                    <NextButton fullWidth />
                  </span>
                </TooltipTrigger>
                {!canGoNext && (
                  <TooltipContent side="top" className="bg-foreground text-background text-[12px] px-2.5 py-1.5 rounded-lg">
                    <p>{tooltipText}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>

        {/* Desktop: Centered layout */}
        <div className="hidden lg:flex items-center justify-center gap-6 relative">
          {/* Left: Step indicator - absolute position */}
          <span className="absolute left-4 text-[12px] text-[#9CA3AF] font-medium">
            {translateInterfaceText("Etapa")}{" "}{currentStep} {" "}{translateInterfaceText("de")}{" "}{totalSteps}
          </span>

          {/* Center: Back + Next */}
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={onBack}
              disabled={!canGoBack}
              className="h-[40px] px-4 bg-white border border-[#E8E8E8] text-[#6B7280] font-medium rounded-[8px] gap-2 text-[13px] disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4" />
              {translateInterfaceText("Voltar")}</Button>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <NextButton />
                  </span>
                </TooltipTrigger>
                {!canGoNext && (
                  <TooltipContent side="top" className="bg-foreground text-background text-[12px] px-2.5 py-1.5 rounded-lg">
                    <p>{tooltipText}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>
    </div>
  );
}
